// EventSource wrapper for the server's /api/sse/stream channel.
//
// Why a custom hook + module instead of dropping EventSource straight into
// each component:
//   • We want a single connection per browser tab, not one per component
//     that subscribes (otherwise opening Inbox + Today + a profile pane
//     spins up three streams against the same userId).
//   • Reconnect backoff lives here so every consumer benefits from the
//     same exponential strategy after a network blip.
//   • Subscribers can listen for specific event names ('message:new',
//     'message:read', 'message:sent') without each one re-parsing JSON.
//
// Tab visibility: the browser keeps EventSource open in background tabs,
// so we don't tear down on tab hide. We DO let the SW handle background
// push notifications separately — SSE is for in-app live updates while
// the user is actively viewing the app.

import { useEffect } from 'react';
import { apiUrl, getNativeSessionToken } from '@/lib/apiBase';

type Listener = (data: any) => void;

let source: EventSource | null = null;
let connecting = false;
const listeners = new Map<string, Set<Listener>>();
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function notify(eventType: string, data: unknown) {
  const set = listeners.get(eventType);
  if (!set) return;
  for (const fn of set) {
    try { fn(data); } catch (e) { console.warn('[messageStream] listener threw:', e); }
  }
}

function connect() {
  if (typeof window === 'undefined') return;
  if (source && source.readyState !== EventSource.CLOSED) return;
  if (connecting) return;
  connecting = true;

  try {
    // On the native app the web assets are served from capacitor://localhost
    // (iOS) / https://localhost (Android), so a relative URL would hit a
    // non-existent local server. apiUrl() points it at the real API origin.
    // EventSource can't send an Authorization header, and native has no
    // session cookie, so pass the native bearer token as a query param —
    // the SSE route reads it back into an Authorization header. Without this
    // the native stream got a 401 and never connected, so live DM updates
    // (and read receipts) never arrived in the app. withCredentials keeps
    // cookie auth working unchanged on the web.
    const token = getNativeSessionToken();
    const base = apiUrl('/api/sse/stream');
    const url = token ? `${base}${base.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : base;
    source = new EventSource(url, { withCredentials: true });
  } catch (e) {
    connecting = false;
    scheduleReconnect();
    return;
  }

  source.addEventListener('open', () => {
    reconnectAttempts = 0;
    connecting = false;
  });

  // Named events from the server: message:new, message:sent, message:read,
  // wins.published (shared Wins feed), and Phase 2c.3 inbox snooze resurfacing.
  for (const t of ['message:new', 'message:sent', 'message:read', 'ready', 'wins.published', 'inbox:resurfaced', 'inbox:resurfaced-dismissed']) {
    source.addEventListener(t, (e: MessageEvent) => {
      let parsed: unknown = null;
      try { parsed = e.data ? JSON.parse(e.data) : null; } catch { /* ignore */ }
      notify(t, parsed);
    });
  }

  source.addEventListener('error', () => {
    connecting = false;
    // EventSource auto-reconnects on most errors, but on hard network drops
    // (iOS PWA backgrounded for > 5min) it falls into the closed state and
    // never recovers. Detect that and force a reconnect with backoff.
    if (source && source.readyState === EventSource.CLOSED) {
      source = null;
      scheduleReconnect();
    }
  });
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  const delay = Math.min(30_000, 1_000 * 2 ** Math.min(reconnectAttempts, 5));
  reconnectAttempts++;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

// Returning to the app must revive a dead stream IMMEDIATELY. iOS kills the
// SSE connection while the app is backgrounded and, on resume, EventSource
// can sit in the CLOSED state without ever firing the 'error' event that the
// reconnect logic above depends on — so live DM updates and badge refreshes
// silently stopped until the user manually reloaded. visibilitychange fires
// in WKWebView when the native app foregrounds, so it covers Capacitor too.
function reviveIfDead() {
  if (listeners.size === 0) return;
  if (source && source.readyState !== EventSource.CLOSED) return;
  source = null;
  reconnectAttempts = 0;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  connect();
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) reviveIfDead();
  });
}
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => reviveIfDead());
}

/**
 * Subscribe to a server-pushed event. Returns an unsubscribe function.
 * Auto-connects the underlying EventSource on first subscription.
 */
export function onMessageEvent(eventType: 'message:new' | 'message:sent' | 'message:read' | 'ready' | 'wins.published' | 'inbox:resurfaced' | 'inbox:resurfaced-dismissed', handler: Listener): () => void {
  if (!listeners.has(eventType)) listeners.set(eventType, new Set());
  listeners.get(eventType)!.add(handler);
  connect();
  return () => {
    listeners.get(eventType)?.delete(handler);
  };
}

/**
 * React hook — re-runs handler with the latest event payload. The handler
 * reference is kept current via a ref-stable wrapper so consumers don't
 * have to memoize. Pass `null` as the handler to disable temporarily.
 */
export function useMessageStream(
  eventType: 'message:new' | 'message:sent' | 'message:read' | 'ready' | 'wins.published' | 'inbox:resurfaced' | 'inbox:resurfaced-dismissed',
  handler: Listener | null,
): void {
  useEffect(() => {
    if (!handler) return;
    return onMessageEvent(eventType, handler);
  }, [eventType, handler]);
}
